<script lang="ts">
  // Etappe-B-Provisorium: Wenn /v2-api/auth/status sagt "auth_required
  // && !authenticated", rendert App.svelte diese Komponente anstelle
  // der Routen. Slice 6 ersetzt das durch eine nichtssagende Pre-Auth-
  // SPA mit eigenem Bundle; bis dahin ist das hier ein gedeckter
  // Trigger fuer den Passkey-Flow plus ein Link auf den v1-Login fuer
  // den unwahrscheinlichen Fall, dass kein Passkey verfuegbar ist.

  import { getJSON } from "./api";
  import { ladeNeu } from "./neuladen";
  import {
    isConditionalMediationAvailable,
    loginWithPasskey,
  } from "./passkey";
  import { useRecoveryCode } from "./recovery";
  import {
    finalizePushLogin,
    formatPushCode,
    startPushLogin,
    streamPushStatus,
    type PushStatus,
  } from "./pushAuth.svelte";

  let busy = $state(false);
  let error = $state<string | null>(null);
  let conditionalAvailable = $state(false);
  // Master-Switch-Probe: pre-login Anfrage an /v2-api/auth/push/available
  // (kein PII, nur bool). Wenn der Master aus ist, blenden wir den
  // Push-Button aus -- damit nicht jeder Besucher der Login-Page Push-
  // Anfragen triggern kann. Backend lehnt zusaetzlich ab + bannt IP.
  let pushAvailable = $state(false);

  // Recovery-Pfad als Fallback wenn der Authenticator weg ist.
  let showRecovery = $state(false);
  let recoveryInput = $state("");

  // Push-Pfad: zweiter Fallback. Erzeugt einen Code, der auf einem
  // bereits eingeloggten Geraet bestaetigt werden muss.
  let showPush = $state(false);
  let pushCode = $state<string | null>(null);
  let pushStatus = $state<PushStatus | null>(null);
  let pushRemaining = $state(0);
  let pushRequestID = $state<number | null>(null);
  let pushCloser: (() => void) | null = null;
  let pushCountdown: ReturnType<typeof setInterval> | null = null;

  function stopPushFlow() {
    pushCloser?.();
    pushCloser = null;
    if (pushCountdown) {
      clearInterval(pushCountdown);
      pushCountdown = null;
    }
  }

  async function onStartPush() {
    if (busy) return;
    busy = true;
    error = null;
    pushStatus = null;
    pushCode = null;
    showPush = true;
    try {
      const res = await startPushLogin();
      if (!res.ok || !res.id || !res.code || !res.expires_at) {
        // Generischer Reject -- Endpoint antwortet konstant in der
        // Zeit, ohne den Grund zu nennen. Ausnahme: wrong_code-Cooldown
        // (siehe Spec) wird mit retry_after_s an das eigene Frontend
        // durchgereicht, damit der User sieht wie lange er warten muss.
        if (typeof res.retry_after_s === "number" && res.retry_after_s > 0) {
          const mins = Math.ceil(res.retry_after_s / 60);
          error =
            res.retry_after_s < 60
              ? `Aus Sicherheitsgruenden gesperrt -- bitte ${res.retry_after_s} s warten und erneut versuchen.`
              : `Aus Sicherheitsgruenden gesperrt -- bitte ${mins} min warten und erneut versuchen.`;
        } else {
          error = "Anmeldung nicht moeglich";
        }
        showPush = false;
        return;
      }
      pushCode = res.code;
      pushRequestID = res.id;
      pushStatus = "pending";
      const expires = new Date(res.expires_at).getTime();
      pushCountdown = setInterval(() => {
        pushRemaining = Math.max(0, Math.round((expires - Date.now()) / 1000));
        if (pushRemaining <= 0) {
          pushStatus = "expired";
          stopPushFlow();
        }
      }, 500);

      pushCloser = streamPushStatus(res.id, async (s) => {
        pushStatus = s;
        if (s === "approved" && pushRequestID != null) {
          try {
            await finalizePushLogin(pushRequestID);
            ladeNeu("anmeldung");
          } catch (e) {
            error = e instanceof Error ? e.message : String(e);
          }
        } else if (s === "rejected" || s === "wrong_code" || s === "expired") {
          stopPushFlow();
        }
      });
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
      showPush = false;
    } finally {
      busy = false;
    }
  }

  function onCancelPush() {
    stopPushFlow();
    showPush = false;
    pushCode = null;
    pushStatus = null;
    pushRequestID = null;
  }

  // Conditional-UI auf Mount probieren: der Browser kann den Passkey
  // automatisch im Autocomplete-Popup vorschlagen (wo bei Passwort-
  // Logins normalerweise gespeicherte Credentials erscheinen). Wenn
  // unterstuetzt, laeuft im Hintergrund ein "wartender" Aufruf, der
  // ohne Klick beendet werden kann. AbortController bricht ab, wenn
  // der User stattdessen den Manual-Button drueckt.
  let conditionalCtl: AbortController | null = null;

  $effect(() => {
    let cancelled = false;
    (async () => {
      // Master-Switch-Probe parallel zur Conditional-Mediation-Pruefung.
      // Fehler ignorieren -- Default ist "Button aus", was Tims Default
      // entspricht (Master off).
      try {
        const r = await getJSON<{ available: boolean }>(
          "/v2-api/auth/push/available",
        );
        if (!cancelled) pushAvailable = !!r.available;
      } catch {
        if (!cancelled) pushAvailable = false;
      }
      const ok = await isConditionalMediationAvailable();
      if (cancelled) return;
      conditionalAvailable = ok;
      if (!ok) return;
      conditionalCtl = new AbortController();
      try {
        await loginWithPasskey("conditional", conditionalCtl.signal);
        // Erfolg: SPA neu laden, damit Auth-State und alle Streams sauber
        // hochkommen. Reload statt Hash-Wechsel, weil Cookies gerade
        // frisch gesetzt sind und die Streams noch ohne sie verbunden
        // waeren.
        ladeNeu("anmeldung");
      } catch (e) {
        // AbortError ist normal (Manual-Button geklickt) -- nicht melden.
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.toLowerCase().includes("abort")) return;
        // Conditional-UI darf still scheitern -- der Manual-Button
        // bleibt sichtbar.
      }
    })();
    return () => {
      cancelled = true;
      conditionalCtl?.abort();
    };
  });

  async function onManual() {
    if (busy) return;
    busy = true;
    error = null;
    conditionalCtl?.abort();
    try {
      await loginWithPasskey("optional");
      ladeNeu("anmeldung");
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  async function onRecoverySubmit(e: Event) {
    e.preventDefault();
    if (busy) return;
    if (!recoveryInput.trim()) return;
    busy = true;
    error = null;
    conditionalCtl?.abort();
    try {
      await useRecoveryCode(recoveryInput.trim());
      ladeNeu("anmeldung");
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      busy = false;
    }
  }
</script>

<div class="grid min-h-dvh place-items-center bg-bg p-6">
  <article
    class="border-border surface grid w-full max-w-sm gap-4 rounded-xl border p-6 text-center"
  >
    <h1 class="text-fg text-base font-semibold">Anmeldung</h1>
    <p class="text-fg-muted text-xs">
      {#if conditionalAvailable}
        Passkey wird im Browser-Vorschlag angeboten. Alternativ:
      {:else}
        Mit Passkey anmelden:
      {/if}
    </p>

    <button
      type="button"
      class="bg-accent/15 text-accent border border-accent/30 rounded-md ctrl-m text-sm font-medium transition disabled:opacity-50"
      disabled={busy}
      onclick={onManual}
    >
      {busy ? "läuft…" : "Mit Passkey anmelden"}
    </button>

    {#if error}
      <div
        class="text-danger t-meta"
      >
        {error}
      </div>
    {/if}

    {#if !showRecovery && !showPush}
      <p class="text-fg-subtle t-meta">
        Kein Passkey?
        <button
          type="button"
          class="text-accent underline-offset-2 hover:underline"
          onclick={() => (showRecovery = true)}
        >Wiederherstellungscode verwenden</button>
        {#if pushAvailable}
          ·
          <button
            type="button"
            class="text-accent underline-offset-2 hover:underline"
            onclick={onStartPush}
          >Per anderem Geraet genehmigen lassen</button>
        {/if}
      </p>
    {:else if showPush}
      <div class="grid gap-3 text-left">
        {#if pushStatus === "pending" && pushCode}
          <p class="text-fg-muted t-meta">
            Auf einem deiner eingeloggten Geraete poppt jetzt ein Banner auf.
            Tippe dort diesen Code ein:
          </p>
          <div
            class="border-accent/40 bg-accent/5 text-fg flex items-center justify-center gap-4 rounded-md border py-4 font-mono text-4xl tracking-widest"
          >
            {formatPushCode(pushCode)}
          </div>
          <p class="text-fg-subtle text-center t-meta tabular-nums">
            laeuft ab in {pushRemaining}s
          </p>
        {:else if pushStatus === "rejected"}
          <p class="text-danger text-xs text-center">
            Anmeldung abgelehnt. Pruefe deine Passkeys.
          </p>
        {:else if pushStatus === "wrong_code"}
          <p class="text-danger text-xs text-center">
            Aus Sicherheitsgruenden abgebrochen, bitte Login neu starten.
          </p>
        {:else if pushStatus === "expired"}
          <p class="text-warning text-xs text-center">
            Abgelaufen ohne Bestaetigung.
          </p>
        {:else if pushStatus === "approved"}
          <p class="text-success text-xs text-center">
            Genehmigt — einen Moment …
          </p>
        {:else}
          <p class="text-fg-subtle text-xs text-center">
            Anfrage wird gesendet …
          </p>
        {/if}
        <button
          type="button"
          class="text-fg-subtle t-meta underline-offset-2 hover:underline"
          onclick={onCancelPush}
        >Zurueck zum Passkey-Login</button>
      </div>
    {:else}
      <form class="grid gap-2 text-left" onsubmit={onRecoverySubmit}>
        <label class="text-fg-muted t-meta" for="recovery-input">
          Recovery-Code
        </label>
        <input
          id="recovery-input"
          type="text"
          spellcheck="false"
          autocomplete="off"
          autocapitalize="characters"
          placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX"
          bind:value={recoveryInput}
          class="border-border bg-bg/60 text-fg rounded-md border px-2 py-1.5 text-center font-mono text-sm tracking-wider"
        />
        <div class="flex items-center justify-between gap-2">
          <button
            type="button"
            class="text-fg-subtle t-meta underline-offset-2 hover:underline"
            onclick={() => {
              showRecovery = false;
              recoveryInput = "";
            }}
          >zurueck</button>
          <button
            type="submit"
            class="bg-accent/15 text-accent border border-accent/30 rounded-md ctrl-m text-xs font-medium transition disabled:opacity-50"
            disabled={busy || !recoveryInput.trim()}
          >Anmelden</button>
        </div>
      </form>
    {/if}
  </article>
</div>
